import type { UserPresence } from "@bezoned/shared/types/api/user/user-presence-get.types.js";
import { Tree } from "fluid-framework";
import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { getUser, getUserPresenceFromFluid } from "../fluid/fluid.service.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";

interface UseUserPresenceProps {
  msUserId: string;
}

/**
 * This hook is used to get the presence of a specific user. To get the presence of the current user, one
 * should use useMyPresence instead.
 */
export const useUserPresence = ({ msUserId }: UseUserPresenceProps) => {
  const { sharedTree } = useFluid();
  const [presence, setPresence] = useState<UserPresence | null>(null);

  // ? We are using msUserId instead of the fluidUserId, because fluidUserId always contains the current user's ID.
  const fluidUser = useMemo(
    () => getUser({ sharedTree, userId: msUserId }),
    [msUserId, sharedTree]
  );

  useEffect(() => {
    let unsubscribe: ReturnType<typeof Tree.on> | null = null;

    const subscribeToPresenceChanges = () => {
      if (!fluidUser) {
        console.log(
          "[useUserPresence]: No fluidUser at the time of subscribing to changes"
        );

        return;
      }

      if (unsubscribe) {
        console.warn(
          "[useUserPresence]: Unsubscribing from previous subscription"
        );

        unsubscribe();
      }

      unsubscribe = Tree.on(fluidUser, "treeChanged", () => {
        const fluidPresence = getUserPresenceFromFluid({
          sharedTree,
          userId: fluidUser.id,
        });

        if (!fluidPresence) {
          console.warn(
            "[useUserPresence]: No fluidPresence found for user",
            fluidUser.id
          );
          return;
        }

        setPresence((previousPresence) =>
          isEqual(previousPresence, fluidPresence)
            ? previousPresence
            : fluidPresence
        );
      });
    };

    // Subscribe to periodic updates
    subscribeToPresenceChanges();

    // Fetch current presence
    if (fluidUser) {
      const fluidPresence = getUserPresenceFromFluid({
        sharedTree,
        userId: fluidUser.id,
      });

      if (fluidPresence) {
        setPresence((previousPresence) =>
          isEqual(previousPresence, fluidPresence)
            ? previousPresence
            : fluidPresence
        );
      }
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [fluidUser, sharedTree]);

  return presence;
};
